import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { numberWithCommas } from "../../../../utils";
import ConfirmModal from "../../../ConfirmModal";
import { formatDiscountPercent } from "../constants";
import PriceChangeList from "./PriceChangeList";
import { PriceChangeRow } from "./priceChangeRows";
import { BookDiscountChange } from "./usePricebookPriceSave";

const NO_LINE_CHANGES = "ไม่มีราคาสินค้าที่เปลี่ยน";

interface ConfirmPriceDialogProps {
  /** Null while closed; the server-priced changes once the preview has answered. */
  rows: PriceChangeRow[] | null;
  bookDiscountChange: BookDiscountChange | null;
  /** Stored lines with no product code, which the save takes off the book. */
  codelessLineCount: number;
  /** Removed lines with a running or upcoming period, which the save deletes along with them. */
  removedPeriodLineCount: number;
  /** Says how far the save reaches: the book's branches and its shops. */
  impactMessage: string;
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * The last look at a price save before it reaches the book's branches: each changed line with
 * what it bills at now, crossed out, and what it will bill at — incl and ex VAT, as the
 * server priced them.
 */
export default function ConfirmPriceDialog({
  rows,
  bookDiscountChange,
  codelessLineCount,
  removedPeriodLineCount,
  impactMessage,
  saving,
  onClose,
  onConfirm,
}: ConfirmPriceDialogProps) {
  const codelessLineWarning =
    codelessLineCount > 0
      ? `รายการที่ไม่มีรหัสสินค้า ${numberWithCommas(codelessLineCount)} รายการจะถูกนำออกจากสมุด`
      : "";
  const removedPeriodWarning =
    removedPeriodLineCount > 0
      ? `รายการที่ลบมีช่วงราคา ${numberWithCommas(removedPeriodLineCount)} รายการ ช่วงราคาเหล่านี้จะถูกลบด้วย`
      : "";

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <ConfirmModal
      open={rows !== null}
      title="ยืนยันการบันทึกราคา"
      description={impactMessage}
      cancelLabel="กลับไปแก้"
      confirmLabel="ยืนยันบันทึก"
      loading={saving}
      onClose={handleClose}
      onConfirm={onConfirm}
    >
      <Stack gap={2} sx={{ mt: 2 }}>
        {codelessLineWarning && (
          <Alert severity="warning">{codelessLineWarning}</Alert>
        )}
        {removedPeriodWarning && (
          <Alert severity="warning">{removedPeriodWarning}</Alert>
        )}
        {bookDiscountChange && (
          <Typography variant="body2">
            ส่วนลดทั้งสมุด{" "}
            <s>{formatDiscountPercent(bookDiscountChange.before)}</s> →{" "}
            <b>{formatDiscountPercent(bookDiscountChange.after)}</b>
          </Typography>
        )}
        <PriceChangeList rows={rows ?? []} emptyText={NO_LINE_CHANGES} />
      </Stack>
    </ConfirmModal>
  );
}
